import type { FC } from 'react';
import { useCallback, useEffect, useState } from 'react';
import { Picture, Rating } from '@ui';
import { userNoAvatar, star_noactive, star, chiefAvatar } from '@images';
import dayjs from 'dayjs';
import Lightbox from '@components/Lightbox';
import { observer } from 'mobx-react';
import type { CommentsItemsFragmentFragment } from '@schema/cart';

const rateMessage = (rating: number): string => {
  if (rating === 1) {
    return 'Ужасный магазин';
  } else if (rating === 2) {
    return 'Плохой магазин';
  } else if (rating === 3) {
    return 'Обычный магазин';
  } else if (rating === 4) {
    return 'Хороший магазин';
  } else if (rating === 5) {
    return 'Отличный магазин';
  }
  return 'Отличный магазин';
};

interface CommentProps {
  hideRateText?: boolean;
  comment: CommentsItemsFragmentFragment;
}

const Comment: FC<CommentProps> = observer(({ hideRateText, comment }) => {
  const avatar = comment?.peopleItem?.attachmentsItem?.cacheItem?.[0]?.filePath;
  const rating = Number(comment.rating);
  const fio = comment?.fio || comment?.peopleItem?.fio || 'Пользователь';
  const { date, text = '', photos, answer } = comment;
  const answerAvatar =
    comment?.peopleItem?.attachmentsItem?.cacheItem?.[0]?.filePath;
  const answerFio = comment?.answerUserItem?.fio;
  const answerDate = comment?.answer_date
    ? dayjs(comment?.answer_date as string | Date).format('DD MMMM YYYY')
    : '';

  const [isOpenLightbox, setIsOpenLightbox] = useState(false);
  const [slide, setSlide] = useState(1);
  const [sources, setSources] = useState([]);
  const [types, setTypes] = useState([]);

  const handlePhotos = useCallback(photos => {
    setSources(photos.map(item => item.big));
    setTypes(photos.map(item => (item.isVideo ? 'video' : 'image')));
  }, []);

  useEffect(() => {
    if (comment?.photos) {
      handlePhotos(comment.photos);
    }
  }, [comment?.photos, handlePhotos]);

  const changeLightboxState = useCallback((index: number) => {
    setIsOpenLightbox(prev => !prev);
    setSlide(index + 1);
  }, []);

  return (
    <div className='comment-container'>
      <Lightbox
        toggler={isOpenLightbox}
        sources={sources}
        slide={slide}
        type='image'
        types={types}
      />

      <div className='comment-block'>
        <div className='avatar'>
          <img src={avatar || userNoAvatar} alt={fio} width='48' />
        </div>
        <div className='comment'>
          <div className='header'>
            <div className='comment-info'>
              <div className='wrap'>
                <div className='name'>{fio}</div>

                <div className='rating-stars'>
                  {rating && rating !== 0 && rating !== null ? (
                    <Rating
                      initialRating={rating}
                      emptySymbol={<Picture icon={star_noactive} height='12' />}
                      fullSymbol={<Picture icon={star} height='12' />}
                      size={12}
                      readonly
                    />
                  ) : (
                    ''
                  )}
                  {!hideRateText && (
                    <span className='rate-message'>
                      {rateMessage(Number(rating))}
                    </span>
                  )}
                </div>
              </div>
              <div className='date'>
                {dayjs(date as string | Date).format('DD MMMM YYYY')}
              </div>
            </div>
          </div>

          {text && (
            <div
              className='mess-content'
              dangerouslySetInnerHTML={{
                __html: text.replace(/(?:\r\n|\r|\n)/g, '<br />')
              }}
            />
          )}

          {!!photos && (
            <div className='media-block'>
              {photos.map((item, index) => (
                <div
                  key={item.id}
                  onClick={() => {
                    changeLightboxState(index);
                  }}
                  className='media-item'
                  style={{
                    background: `url("${item.thumb}") no-repeat 50% 50% / cover`
                  }}
                />
              ))}
            </div>
          )}
        </div>
      </div>

      {answer && (
        <div className='comment-block answer'>
          <div className='avatar'>
            <Picture icon={answerAvatar || chiefAvatar} height='48' />
          </div>
          <div className='comment'>
            <div className='header'>
              <div className='comment-info'>
                <div className='name'>{answerFio || 'Деликатеска'}</div>
                <div className='date'>{answerDate}</div>
              </div>
            </div>
            <div
              className='mess-content'
              dangerouslySetInnerHTML={{
                __html: answer.replace(/(?:\r\n|\r|\n)/g, '<br />')
              }}
            />
          </div>
          <span className='corner' />
        </div>
      )}
    </div>
  );
});

export default Comment;
